// AppBanner.jsx
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Dashboard.css';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';

const AppBanner = forwardRef(({ onLogout }, ref) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [practice, setPractice] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const dropdownRef = useRef(null);
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();

  const fetchUsage = useCallback(async () => {
    setLoadingUsage(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        setUsage(null);
        setPractice(null);
        setLoadingUsage(false);
        return;
      }

      // Fetch usage data directly from Supabase
      const today = new Date().toISOString().slice(0, 10);
      const { data: usage, error } = await supabase
        .from('user_daily_usage')
        .select('ai_words_used, ai_practice_used')
        .eq('user_id', session.user.id)
        .eq('date', today)
        .maybeSingle();

      if (error) {
        console.error('Error fetching usage data:', error);
      }

      const usageData = usage || { ai_words_used: 0, ai_practice_used: 0 };

      // Fetch premium status and limits from user_data
      const { data: userData, error: userError } = await supabase
        .from('user_data')
        .select('premium_expires_at, max_words_daily, max_ai_practice_daily')
        .eq('id', session.user.id)
        .single();

      if (userError) {
        console.error('Error fetching user data:', userError);
      }

      const isPremium = userData?.premium_expires_at && new Date(userData.premium_expires_at) > new Date();

      // Use dynamic limits from DB, fallback to hardcoded if null
      let wordCap = userData?.max_words_daily !== null && userData?.max_words_daily !== undefined
        ? userData.max_words_daily
        : (isPremium ? 100 : 10);

      let practiceCap = userData?.max_ai_practice_daily !== null && userData?.max_ai_practice_daily !== undefined
        ? userData.max_ai_practice_daily
        : (isPremium ? 20 : 3);

      // OVERRIDE: If premium is expired, enforce free-tier limits regardless of database values
      if (!isPremium) {
        wordCap = 10;
        practiceCap = 3;
      }

      setUsage({
        wordsUsed: usageData.ai_words_used || 0,
        dailyLimit: wordCap,
        premium: isPremium
      });
      setPractice({
        practiceUsed: usageData.ai_practice_used || 0,
        dailyPracticeLimit: practiceCap,
        premium: isPremium
      });
    } catch (e) {
      console.error('Error fetching usage:', e);
      setUsage(null);
      setPractice(null);
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  useImperativeHandle(ref, () => ({ fetchUsage }), [fetchUsage]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchUsage();
    }
  }, [fetchUsage]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      fetchUsage();
    };

    window.addEventListener('refreshUsage', handleRefresh);
    return () => {
      window.removeEventListener('refreshUsage', handleRefresh);
    };
  }, [fetchUsage]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  const safe = (v, fallback = 0) => (typeof v === 'number' && v >= 0 ? v : fallback);
  const isPremium = usage?.premium === true || practice?.premium === true;
  const wordsUsed = safe(usage?.wordsUsed ?? usage?.ai_words_used, 0);
  const wordsLimit = safe(usage?.dailyLimit, isPremium ? 100 : 10);
  const practiceUsed = safe(practice?.practiceUsed, 0);
  const practiceLimit = safe(practice?.dailyPracticeLimit, isPremium ? 20 : 3);

  const combinedPill = (
    <div className="pill combined-pill">
      <div>{t('bannerWords')}: {wordsUsed}/{wordsLimit}</div>
      <div>{t('aiAbbreviation')} {t('bannerPractice')}: {practiceUsed}/{practiceLimit}</div>
    </div>
  );

  const pills = (
    <>
      <div className="pill pill-shrinkable">{t('bannerWords')}: {wordsUsed}/{wordsLimit}</div>
      <div className="pill pill-shrinkable">{t('aiAbbreviation')} {t('bannerPractice')}: {practiceUsed}/{practiceLimit}</div>
    </>
  );

  return (
    <>
      <header className="app-banner" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="app-banner-content">
          <Link to="/" className="app-banner-title">{t('appTitle')}</Link>
          {onLogout && (
            <div className="usage-container">
              {loadingUsage ? (
                <>
                  <div className="pill-group pill-group-wide">
                    <div className="pill pill-shrinkable skeleton">Words: 00/00</div>
                    <div className="pill pill-shrinkable skeleton">AI Practice: 00/00</div>
                  </div>
                  <div className="pill-group pill-group-merged">
                    <div className="pill combined-pill skeleton">
                      <div>Words: 00/00</div>
                      <div>AI Practice: 00/00</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="pill-group pill-group-wide">{pills}</div>
                  <div className="pill-group pill-group-merged">{combinedPill}</div>
                </>
              )}
            </div>
          )}
          <div className="app-banner-profile" ref={dropdownRef}>
            <button className="app-banner-profile-btn" onClick={() => setDropdownOpen((open) => !open)} aria-label={t('openProfileMenu')}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="4" stroke="#386641" strokeWidth="2" fill="#fff" />
                <ellipse cx="12" cy="17" rx="7" ry="4" stroke="#386641" strokeWidth="2" fill="#fff" />
              </svg>
            </button>
            {dropdownOpen && (
              <div className="app-banner-dropdown">
                {onLogout ? (
                  <>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/profile'); }}>{t('profile')}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/'); }}>{t('myModules')}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/classrooms'); }}>{t('classrooms') || 'Classrooms'}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/home'); }}>{t('home')}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/store'); }}>{t('store')}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/support'); }}>{t('supportTitle')}</button>
                    <button className="app-banner-dropdown-item" onClick={onLogout}>{t('logOut')}</button>
                  </>
                ) : (
                  <>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/home'); }}>{t('home')}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/about'); }}>{t('about')}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/privacy'); }}>{t('privacyPolicy')}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/terms'); }}>{t('termsOfService')}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/support'); }}>{t('supportTitle')}</button>
                    <button className="app-banner-dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/login'); }}>{t('logIn')}</button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
      <style>{`
        .usage-container {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .pill {
          background: #e9f5e1;
          color: #386641;
          border-radius: 999px;
          padding: 4px 10px;
          font-weight: 600;
          font-size: 1rem;
          border: 1px solid #b7e4c7;
          box-shadow: 0 1px 4px rgba(56,102,65,0.06);
          white-space: nowrap;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 60px;
          height: 28px;
          text-align: center;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
          transition: font-size 0.3s ease, padding 0.3s ease;
        }
        .pill-group-wide {
          display: flex;
          gap: 8px;
        }
        .pill-group-merged {
          display: none;
        }
        .combined-pill {
          flex-direction: column;
          padding: 6px 10px;
          line-height: 1.2;
          min-width: 120px;
        }
        .skeleton {
          animation: pulse 1.2s infinite;
          opacity: 0.7;
          color: transparent;
          background: #d8ead5;
          user-select: none;
        }
        @keyframes pulse {
          0% { opacity: 0.7; }
          50% { opacity: 0.3; }
          100% { opacity: 0.7; }
        }
        @media (max-width: 460px) {
          .pill {
            font-size: clamp(0.55rem, 2.2vw, 1rem);
            padding: 2px 6px;
          }
        }
        @media (max-width: 370px) {
          .pill-group-wide {
            display: none;
          }
          .pill-group-merged {
            display: flex;
          }
        }
      `}</style>
    </>
  );
});

export default AppBanner;
